import React from 'react';
import {
  ArrowRight,
  Check,
  ClipboardList,
  Factory,
  Gauge,
  MapPinned,
  Send,
  Sparkles,
  Waves,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '../../../lib/utils';

export const GENERATION_WORKFLOW_STAGE_IDS = [
  'requirements',
  'impacts',
  'equipment',
  'capacity',
  'parcel',
  'handoff',
] as const;

export type GenerationWorkflowStageId = (typeof GENERATION_WORKFLOW_STAGE_IDS)[number];

export type GenerationWorkflowStageTone =
  | 'complete'
  | 'current'
  | 'attention'
  | 'blocked'
  | 'neutral';

export interface GenerationWorkflowStage<Id extends GenerationWorkflowStageId = GenerationWorkflowStageId> {
  id: Id;
  label: string;
  status: string;
  tone: GenerationWorkflowStageTone;
}

export type GenerationWorkflowStages = readonly [
  GenerationWorkflowStage<'requirements'>,
  GenerationWorkflowStage<'impacts'>,
  GenerationWorkflowStage<'equipment'>,
  GenerationWorkflowStage<'capacity'>,
  GenerationWorkflowStage<'parcel'>,
  GenerationWorkflowStage<'handoff'>,
];

export interface GenerationWorkflowNextAction {
  title: string;
  description: string;
  target: GenerationWorkflowStageId;
  /**
   * Exact-outcome chip (e.g. "Clears N-1 + reserve"). Only supply when the
   * outcome is a real solver result, never an estimate — mirrors the
   * Path-to-Go rule that band-impact chips are oracle re-runs only.
   */
  impact?: string;
  /**
   * One-click apply affordance (the Intent-prefill pattern). `title` carries
   * the full rationale for the tooltip; `onApply` dispatches the same guarded
   * apply path the owning section uses — no new write path.
   */
  apply?: {
    label: string;
    title: string;
    onApply: () => void;
  };
}

export interface GenerationWorkflowNavigatorProps {
  stages: GenerationWorkflowStages;
  recommendedNextAction: GenerationWorkflowNextAction;
  /**
   * The stage panel currently shown by the host. Renders the matching pill in
   * its selected treatment. Independent of `recommendedNextAction.target` —
   * "where you are" and "what to do next" are distinct signals.
   */
  activeStage?: GenerationWorkflowStageId | null;
  /**
   * Staged-panel mode: intercept stage/action links and switch panels instead
   * of scrolling. Links keep their hrefs (semantics + tests + no-JS fallback);
   * the handler preventDefaults and owns the transition.
   */
  onSelectStage?: (stage: GenerationWorkflowStageId) => void;
  /**
   * Where the "Do this next" block lives. `inline` (default) renders it as the
   * navigator's right header column. `external` drops it — the host renders a
   * `GenerationNextActionCard` elsewhere (the Option-B status rail) — and the
   * header collapses to a single slim row (eyebrow + title + progress).
   * `recommendedNextAction` is still required: it drives `aria-current` on the
   * stage pills in both placements.
   */
  nextActionPlacement?: 'inline' | 'external';
  ariaLabel?: string;
  className?: string;
}

const STAGE_ICONS: Record<GenerationWorkflowStageId, LucideIcon> = {
  requirements: ClipboardList,
  capacity: Gauge,
  equipment: Factory,
  impacts: Waves,
  parcel: MapPinned,
  handoff: Send,
};

/** One-line stage purposes (the Path-to-Go STAGE_PURPOSE pattern) — surfaced
 *  as tooltips so the compact cards stay scannable. */
const STAGE_PURPOSE: Record<GenerationWorkflowStageId, string> = {
  requirements: 'Set facility demand, operating hours and the outage duty.',
  impacts: 'Compare modeled resource demand with source-backed site limits.',
  equipment: 'Review grid feeds or on-site equipment and the evidence behind them.',
  capacity: 'Compare modeled supply with facility demand and reserve under declared outages.',
  parcel: 'Place the equipment and check its fit within the parcel.',
  handoff: 'Save and select a scenario, publish its layout, then rebuild evidence for reports.',
};

/** Stage tone → progress-track segment colour. Neutral stays border-toned so
 *  the track reads as "not reached", never as a verdict. */
const TONE_SEGMENT: Record<GenerationWorkflowStageTone, string> = {
  complete: 'bg-success',
  current: 'bg-primary',
  attention: 'bg-warning',
  blocked: 'bg-danger',
  neutral: 'bg-border',
};

function stageTarget(id: GenerationWorkflowStageId): string {
  return `#generation-stage-${id}`;
}

export interface GenerationNextActionCardProps {
  action: GenerationWorkflowNextAction;
  onActivate: (stage: GenerationWorkflowStageId) => void;
  className?: string;
  label?: string;
}

/**
 * The "Do this next" block — the single recommended action with its optional
 * exact-outcome chip and one-click apply. One markup source for both homes:
 * embedded as the navigator's right header column (`inline` placement) or as
 * a standalone card in the Option-B status rail (`external` placement). The
 * wrapper keeps the `generation-workflow-next-action` testid wherever it
 * renders; only one placement exists at a time.
 */
export function GenerationNextActionCard({
  action,
  onActivate,
  className,
  label = 'Do this next',
}: GenerationNextActionCardProps): React.JSX.Element {
  return (
    <div
      className={className}
      data-testid="generation-workflow-next-action"
      data-target-stage={action.target}
    >
      <p className="flex items-center gap-1.5 text-xs font-medium text-text-secondary">
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        {label}
      </p>
      <button
        type="button"
        onClick={() => onActivate(action.target)}
        className="mt-1.5 inline-flex max-w-full items-center gap-2 text-left text-sm font-bold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <span>{action.title}</span>
        <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
      </button>
      <p className="mt-1 text-xs leading-relaxed text-text-muted">
        {action.description}
      </p>
      {(action.impact || action.apply) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {action.impact && (
            <span
              className="border border-success/50 bg-success/10 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-success rounded-md"
              data-testid="generation-next-action-impact"
            >
              {action.impact}
            </span>
          )}
          {action.apply && (
            <button
              type="button"
              onClick={action.apply.onApply}
              title={action.apply.title}
              className="inline-flex items-center gap-1 border border-primary bg-primary px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md"
              data-testid="generation-next-action-apply"
            >
              <Sparkles className="h-3 w-3" aria-hidden />
              {action.apply.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Presents the six Generation & Impact workflow stages and links each stage to
 * its corresponding page section. The caller owns all workflow and solver state.
 */
export function GenerationWorkflowNavigator({
  stages,
  recommendedNextAction,
  activeStage = null,
  onSelectStage,
  nextActionPlacement = 'inline',
  ariaLabel = 'Generation & impact stages',
  className,
}: GenerationWorkflowNavigatorProps): React.JSX.Element {
  const headingId = React.useId();
  const tabRefs = React.useRef<Partial<Record<GenerationWorkflowStageId, HTMLButtonElement | null>>>({});
  const displayedStage = activeStage ?? recommendedNextAction.target;
  const clearedCount = stages.filter((stage) => stage.tone === 'complete').length;
  const blockedCount = stages.filter((stage) => stage.tone === 'blocked').length;

  // Buttons, not anchors (cockpit contract rule 5 — the Path-to-GO tablist
  // pattern): activate through the host's panel switcher when wired, else
  // fall back to scrolling the target section into view.
  const activateStage = (stage: GenerationWorkflowStageId) => {
    if (onSelectStage) {
      onSelectStage(stage);
      return;
    }
    try {
      const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      document.querySelector(stageTarget(stage))?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    } catch {
      /* best-effort scroll fallback */
    }
  };

  return (
    <section
      className={cn('rounded-2xl border border-border bg-card shadow-card', className)}
      aria-labelledby={headingId}
      data-testid="generation-workflow-navigator"
    >
      {/* Honest stage-count progress: a segmented track (one square segment
          per stage, coloured by its tone) + the "N of 6" tally. No derived
          percentages — stages are the only unit this meter speaks. Shared by
          both header variants below. */}
      {(() => {
        const progressMeter = (
          <>
            <p
              className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em] text-text-muted"
              data-testid="generation-workflow-progress-tally"
            >
              {clearedCount}/{stages.length} complete
              {blockedCount > 0 ? ` · ${blockedCount} blocked` : ''}
            </p>
            <div className="flex min-w-0 flex-1 overflow-hidden rounded-full" aria-hidden>
              {stages.map((stage) => (
                <span
                  key={stage.id}
                  className={cn('h-1.5 flex-1', TONE_SEGMENT[stage.tone])}
                  data-progress-segment={stage.id}
                  data-tone={stage.tone}
                />
              ))}
            </div>
          </>
        );

        if (nextActionPlacement === 'external') {
          // Slim header: the host renders the next-action card elsewhere, so
          // one row (eyebrow/title + progress) is all the chrome this earns.
          return (
            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b border-border px-4 py-3 sm:px-5">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
                  Generation &amp; Impact
                </p>
                <h2 id={headingId} className="mt-0.5 text-base font-bold text-text-primary">
                  Scenario workflow
                </h2>
              </div>
              <div
                className="flex min-w-[14rem] max-w-md flex-1 items-center gap-3 pb-1.5"
                data-testid="generation-workflow-progress"
              >
                {progressMeter}
              </div>
            </div>
          );
        }

        return (
          <div className="grid border-b border-border lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.72fr)]">
            <div className="px-4 py-3 sm:px-5 sm:py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
                Generation &amp; Impact
              </p>
              <h2
                id={headingId}
                className="mt-1 text-base font-bold text-text-primary"
              >
                Scenario workflow
              </h2>
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-text-muted">
                Define the duty, review supply and impacts, fit the equipment, then hand the saved basis to reports.
              </p>
              <div className="mt-3 flex max-w-2xl items-center gap-3" data-testid="generation-workflow-progress">
                {progressMeter}
              </div>
            </div>

            <GenerationNextActionCard
              action={recommendedNextAction}
              onActivate={activateStage}
              className="border-t border-primary/40 bg-primary/[0.04] px-4 py-3 sm:px-5 sm:py-4 lg:border-l lg:border-t-0"
            />
          </div>
        );
      })()}

      <nav aria-label={ariaLabel}>
        <div className="max-w-full px-3 py-3 sm:px-4" data-testid="generation-workflow-stage-grid">
          {/* PILL RAIL — restyled to the Project-Intent Focused-Stepper
              grammar (one visual language for step rails): monochrome
              primary-only pills, h-5 numbered disc (primary-filled check when
              complete, glow when active), gradient hairline connectors,
              type-meta labels. Severity tones stay OFF the pills — the
              segmented progress track, open-items rail, and each pill's
              sr-only/tooltip status carry them. The pill for the panel the
              user is LOOKING AT gets the active treatment; aria-current stays
              on the RECOMMENDED stage (two distinct signals). Wraps instead
              of overflowing so every stage stays reachable without horizontal
              discovery (cockpit contract — unlike the Intent rail's
              single-row scroll). */}
          <ol className="flex flex-wrap items-center gap-x-1 gap-y-2" role="tablist" aria-label={ariaLabel}>
            {stages.map((stage, index) => {
              const StageIcon = STAGE_ICONS[stage.id];
              const isRecommended = stage.id === recommendedNextAction.target;
              const isActive = stage.id === displayedStage;
              const isComplete = stage.tone === 'complete';

              return (
                <li key={stage.id} className="flex items-center" role="presentation">
                  {index > 0 && (
                    <span
                      aria-hidden
                      className="mr-1 hidden h-px w-4 shrink-0 bg-border-subtle sm:inline-block"
                    />
                  )}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    ref={(node) => { tabRefs.current[stage.id] = node; }}
                    onKeyDown={(event) => {
                      let next: number;
                      if (event.key === 'ArrowRight') next = (index + 1) % stages.length;
                      else if (event.key === 'ArrowLeft') next = (index + stages.length - 1) % stages.length;
                      else if (event.key === 'Home') next = 0;
                      else if (event.key === 'End') next = stages.length - 1;
                      else return;
                      event.preventDefault();
                      const target = stages[next]!.id;
                      activateStage(target);
                      tabRefs.current[target]?.focus();
                    }}
                    onClick={() => activateStage(stage.id)}
                    aria-current={isRecommended ? 'step' : undefined}
                    aria-label={`Stage ${index + 1}: ${stage.label}. ${stage.status}`}
                    title={`${STAGE_PURPOSE[stage.id]} — ${stage.status}`}
                    className={cn(
                      'group inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                      isActive
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-border-subtle text-text-secondary hover:border-border hover:text-text-primary',
                    )}
                    data-testid={`generation-workflow-stage-${stage.id}`}
                    data-tone={stage.tone}
                    data-active={isActive ? 'true' : undefined}
                  >
                    <span
                      className={cn(
                        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold',
                        isComplete
                          ? 'border-primary bg-primary text-primary-foreground'
                          : isActive
                            ? 'border-primary text-primary'
                            : 'border-border-subtle text-text-muted',
                      )}
                      aria-hidden
                    >
                      {isComplete ? <Check className="h-3 w-3" /> : index + 1}
                    </span>
                    <StageIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="type-meta whitespace-nowrap">
                      {stage.label}
                    </span>
                    {/* Status stays in the accessibility tree + tooltip; the
                        progress track and open items carry it visually. */}
                    <span className="sr-only">{stage.status}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
      <p className="border-t border-border px-4 py-2.5 text-xs leading-relaxed text-text-secondary" data-testid="generation-stage-purpose">{STAGE_PURPOSE[displayedStage]}</p>
    </section>
  );
}
